import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useAppDispatch } from '@/hooks/useReduxHooks';
import {
  abandonAuthorisation,
  getVenueMonetisation,
} from '@/store/thunkMidddleware/monetisationMiddleware';
import { openSubscriptionCheckout } from '@/lib/razorpaySubscription';
import PlanSwitcherModal from '@/components/turf/PlanSwitcherModal';
import SubscriptionActivationModal, {
  ActivationStatus,
} from '@/components/turf/SubscriptionActivationModal';

/**
 * Switching one venue's plan, wherever the vendor is standing.
 *
 * The switcher itself is a dialog, but the work around it is not: a mandate is
 * authorised in Razorpay and the venue only flips on the `subscription.charged`
 * webhook, so the plan has to be polled rather than assumed, and a checkout
 * that closes without an authorisation still has to be checked before it is
 * written off. That sequence is identical everywhere a plan can be switched, so
 * it lives here rather than being re-typed next to each entry point.
 *
 * `resolveVenueName` exists only for the retry path: when a closed checkout
 * turns out to be unpaid the switcher is reopened, and by then the venue is an
 * id carried out of Razorpay's notes, not the one the caller named.
 */

const POLL_INTERVAL_MS = 2500;
const POLL_ATTEMPTS = 12;
/**
 * After a checkout closes without an authorisation: checks at 0s, 2s and 4s,
 * then the abandon call, so the "checking payment status" dialog is up for
 * about 5 seconds.
 */
const QUIET_POLL_ATTEMPTS = 3;
const QUIET_POLL_INTERVAL_MS = 2000;

interface SwitcherTarget {
  id: string;
  name?: string;
}

export interface PlanSwitcher {
  /** Open the switcher for one venue. */
  openSwitcher: (venueId: string, venueName?: string) => void;
  /** Hand a Razorpay subscription checkout here; activation is driven from it. */
  handleSubscriptionCheckout: (checkout: Record<string, any>) => void;
  /** True while a payment is being confirmed — callers show a quiet spinner. */
  verifying: boolean;
  /** Render once, anywhere in the caller's tree. */
  modals: React.ReactElement;
}

export const usePlanSwitcher = (
  resolveVenueName?: (venueId: string) => string | undefined,
): PlanSwitcher => {
  const dispatch = useAppDispatch();

  const [target, setTarget] = useState<SwitcherTarget | null>(null);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  // The vendor has paid and the plan has not flipped yet — this drives the
  // blocking "validating your subscription" dialog, not just a spinner.
  const [activation, setActivation] = useState<ActivationStatus | null>(null);
  const [pollAttempt, setPollAttempt] = useState(0);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollVenueRef = useRef<string | null>(null);
  /** A late reply from an earlier poll must not settle a newer one. */
  const pollTokenRef = useRef(0);

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  const openSwitcher = (venueId: string, venueName?: string) => {
    if (!venueId) return;
    setTarget({ id: venueId, name: venueName });
    setSwitcherOpen(true);
  };

  /**
   * The mandate is authorised in Razorpay but the venue only moves to
   * SUBSCRIPTION on the `subscription.charged` webhook, so the plan is polled
   * rather than assumed.
   */
  const pollUntilSubscribed = (subscribedVenueId: string) => {
    if (!subscribedVenueId) return;
    pollVenueRef.current = subscribedVenueId;
    const token = ++pollTokenRef.current;
    setActivation('verifying');
    setPollAttempt(0);
    let attempts = 0;

    const stop = () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };

    // The first read goes out immediately — the webhook has often landed by the
    // time checkout closes, and waiting an interval to find that out just makes
    // the dialog sit there.
    const check = () => {
      attempts += 1;
      setPollAttempt(attempts);
      dispatch(
        getVenueMonetisation({
          venueId: subscribedVenueId,
          callback: (res: any) => {
            if (token !== pollTokenRef.current) return;
            if (res?.payload?.plan === 'SUBSCRIPTION') {
              stop();
              setActivation('active');
            } else if (attempts >= POLL_ATTEMPTS) {
              stop();
              setActivation('slow');
            }
          },
        }),
      );
    };

    stop();
    check();
    pollRef.current = setInterval(check, POLL_INTERVAL_MS);
  };

  /**
   * Razorpay's checkout closed without an authorisation. That is not proof
   * nothing was paid — a UPI Autopay mandate can be approved in the UPI app
   * while the window reports a dismissal — so the plan is checked behind a
   * dialog first, then the pending authorisation is abandoned. The server
   * re-checks Razorpay before cancelling, so a mandate that landed in the gap
   * is activated rather than torn down.
   */
  const checkAfterDismiss = (subscribedVenueId: string, failure?: string) => {
    if (!subscribedVenueId) return;
    const token = ++pollTokenRef.current;
    setActivation('checking');
    let attempts = 0;

    const stop = () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };

    const giveUp = () => {
      dispatch(
        abandonAuthorisation({
          venueId: subscribedVenueId,
          reason: 'ABANDONED_AT_CHECKOUT',
          callback: (res: any) => {
            if (token !== pollTokenRef.current) return;
            const outcome = res?.payload?.outcome;
            dispatch(getVenueMonetisation({ venueId: subscribedVenueId }));

            if (outcome === 'ACTIVATED' || outcome === 'AUTHORISED') {
              setActivation('active');
              return;
            }

            // Not paid: hand the vendor back to the switcher to try again.
            setActivation(null);
            if (failure) toast.error(failure);
            openSwitcher(
              subscribedVenueId,
              resolveVenueName?.(subscribedVenueId) ??
                (subscribedVenueId === target?.id ? target?.name : undefined),
            );
          },
        }),
      );
    };

    const check = () => {
      attempts += 1;
      dispatch(
        getVenueMonetisation({
          venueId: subscribedVenueId,
          callback: (res: any) => {
            if (token !== pollTokenRef.current) return;
            if (res?.payload?.plan === 'SUBSCRIPTION') {
              stop();
              setActivation('active');
            } else if (attempts >= QUIET_POLL_ATTEMPTS) {
              stop();
              giveUp();
            }
          },
        }),
      );
    };

    stop();
    check();
    pollRef.current = setInterval(check, QUIET_POLL_INTERVAL_MS);
  };

  const closeActivation = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
    pollTokenRef.current += 1;
    setActivation(null);
    setPollAttempt(0);
  };

  /** "Check again" from the slow state — the payment is done, only the flip is late. */
  const retryActivation = () => {
    if (pollVenueRef.current) pollUntilSubscribed(pollVenueRef.current);
  };

  const handleSubscriptionCheckout = (checkout: Record<string, any>) => {
    // The subscribe route stamps the venue into the checkout notes, so the
    // right venue is polled whichever modal started this.
    const subscribedVenueId = checkout?.notes?.venueId || target?.id;
    // Razorpay can fire ondismiss as the sheet tears down after a successful
    // authorisation; without this the vendor gets "checkout closed" over the
    // dialog that is confirming the payment they just made.
    let authorised = false;
    // Razorpay keeps its window open after a failed attempt so the vendor can
    // retry; the reason is held and only shown if the check finds no payment.
    let failure: string | undefined;
    try {
      openSubscriptionCheckout(checkout, {
        onAuthorised: () => {
          authorised = true;
          pollUntilSubscribed(subscribedVenueId);
        },
        onDismiss: () => {
          if (!authorised) checkAfterDismiss(subscribedVenueId, failure);
        },
        onFailed: (res: any) => {
          failure = res?.error?.description || 'The mandate could not be authorised';
        },
      });
    } catch {
      toast.error('Payment could not start. Please refresh and try again.');
    }
  };

  const modals = (
    <>
      <PlanSwitcherModal
        open={switcherOpen}
        onOpenChange={(isOpen) => {
          setSwitcherOpen(isOpen);
          if (!isOpen) setTarget(null);
        }}
        venueId={target?.id ?? null}
        venueName={target?.name}
        onSubscriptionCheckout={handleSubscriptionCheckout}
      />
      <SubscriptionActivationModal
        status={activation}
        venueName={
          (pollVenueRef.current ? resolveVenueName?.(pollVenueRef.current) : undefined) ??
          target?.name
        }
        attempt={pollAttempt}
        totalAttempts={POLL_ATTEMPTS}
        onClose={closeActivation}
        onRetry={retryActivation}
      />
    </>
  );

  return {
    openSwitcher,
    handleSubscriptionCheckout,
    verifying: activation === 'verifying' || activation === 'checking',
    modals,
  };
};

export default usePlanSwitcher;
